import React, { FC, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormInputText } from '../../../../../inputs/FormInputText';
import { CustomMUIButton } from '../../../../buttons/CustomMUIButton';
import { ButtonClassNames, InputTextFormat, InputRegexPattern } from '../../../../../../models/Common';
import { formatPhone } from '../../../../../../utils';
import { userInfoApi } from '../../../../../../services/apiServices/user/userInfo';
import { useCustomSnackbar } from '../../../../../../hooks/useCustomEnqueueSnackbar';
import { ApiError } from '../../../../../../models/ApiError';

interface ChangeBackupPhoneProps {
  phone: string;
  id: number;
  handleClose: () => void;
}

export const EditBackupPhone: FC<ChangeBackupPhoneProps> = ({ phone, id, handleClose }) => {
  const { t } = useTranslation();
  const methods = useForm<{ phoneNumber: string; phoneId: number }>();
  const { handleSubmit, setError, setValue } = methods;
  const { customEnqueueSnackbar } = useCustomSnackbar();
  const [editBackupPhoneApi, { isLoading }] = userInfoApi.useEditBackupPhoneMutation();

  useEffect(() => {
    setValue('phoneNumber', phone);
    setValue('phoneId', id);
  }, [phone, id]);

  const handleEditPhone = async ({ phoneNumber, phoneId }: { phoneNumber: string; phoneId: number }) => {
    try {
      await editBackupPhoneApi({ phone: formatPhone(phoneNumber), phoneId })
        .unwrap()
        .then(() => {
          customEnqueueSnackbar(t('snackbar.backupPhoneNumberChangedSuccessfully'), 'success');
          handleClose();
        });
    } catch (err) {
      const error = err as ApiError;
      setError('phoneNumber', {
        type: 'server',
        message: error?.data?.error.message,
      });
    }
  };

  return (
    <div className="modals">
      <div className="modals__header">
        <article className="modals__header__title">{t('accountSettings.editingBackupPhoneNumber')}</article>
      </div>
      <form className="modals__form" onSubmit={handleSubmit(handleEditPhone)}>
        <FormProvider {...methods}>
          <FormInputText
            name="phoneNumber"
            className="primary"
            label={t('input.phone')}
            isRequired
            format={InputTextFormat.Phone}
            pattern={InputRegexPattern.Phone}
            type="tel"
          />
          <div className="modals__btn-group">
            <CustomMUIButton
              label={t('buttons.save')}
              type="submit"
              className={ButtonClassNames.PrimaryFulfilled}
              isBold
              isLoading={isLoading}
            />
          </div>
        </FormProvider>
      </form>
    </div>
  );
};
